
import React, { useState } from 'react';
import { Send, CheckCircle2, ShieldCheck, Loader2, MessageSquare } from 'lucide-react';

const WaitlistSection: React.FC = () => {
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
    region: '',
    message: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('loading');

    // 1. Preparar mensagem para WhatsApp
    const waNumber = "5531989897034";
    const waText = `*SOLICITAÇÃO DE ACESSO TÁTICO - GURU.VOTO*%0A%0A` +
                   `*Nome:* ${formData.name}%0A` +
                   `*Email:* ${formData.email}%0A` +
                   `*Cargo:* ${formData.role}%0A` +
                   `*Região:* ${formData.region}%0A` +
                   `*Motivo:* ${formData.message}`;

    try {
      // 2. Enviar para Google Sheets (URL de Produção do Usuário)
      const SHEET_WEBHOOK_URL = "https://script.google.com/macros/s/AKfycbxVeToPSMLtc62znCIiA9YrTgyhFszA0i9KlbQAPfATdb_PnD3cfo6Somg-ja8cSW9vBA/exec";
      
      // Enviamos via fetch (modo no-cors para evitar bloqueios de pre-flight simples em Apps Script)
      await fetch(SHEET_WEBHOOK_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          timestamp: new Date().toLocaleString('pt-BR'),
          ...formData,
          source: 'Landing Page'
        })
      });

      // Simulação de delay para imersão no tema "War Room"
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setFormState('success');

      // 3. Abrir WhatsApp em nova aba
      window.open(`https://wa.me/${waNumber}?text=${waText}`, '_blank');
      
    } catch (error) {
      console.error("Erro na transmissão tática:", error);
      // Fallback: Abre o WhatsApp mesmo que o fetch falhe por motivos de rede
      setFormState('success');
      window.open(`https://wa.me/${waNumber}?text=${waText}`, '_blank');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (formState === 'success') {
    return (
      <section id="waitlist" className="py-24 px-4 bg-black">
        <div className="max-w-3xl mx-auto text-center bg-zinc-950 border border-[#c17c0c]/30 p-12 rounded-3xl relative overflow-hidden">
          <div className="scanline"></div>
          <div className="w-20 h-20 bg-[#c17c0c]/10 rounded-full flex items-center justify-center mx-auto mb-8 animate-pulse">
            <CheckCircle2 className="text-[#c17c0c]" size={40} />
          </div>
          <h2 className="text-3xl font-bold mb-4 uppercase italic tracking-tighter">Transmissão Concluída</h2>
          <p className="text-zinc-400 mb-8 leading-relaxed font-light">
            Seus dados foram inseridos na fila de processamento prioritário (Google Cloud) e o canal de WhatsApp foi aberto. Nossa equipe entrará em contato em breve.
          </p>
          <div className="flex flex-col items-center space-y-4">
            <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-lg text-[10px] font-mono text-[#c17c0c] uppercase tracking-[0.2em]">
              TICKET: URGENT_DISPATCH | STATUS: SYNCED_SHEETS
            </div>
            <button 
              onClick={() => setFormState('idle')}
              className="text-zinc-600 hover:text-white text-[10px] uppercase font-mono tracking-widest underline underline-offset-4"
            >
              Nova Solicitação
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="waitlist" className="py-24 px-4 bg-black">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <div className="flex items-center space-x-2 text-[#c17c0c] mb-4">
              <ShieldCheck size={20} />
              <span className="text-sm font-mono uppercase tracking-widest font-bold">Protocolo de Entrada</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black mb-6 uppercase italic tracking-tighter leading-none">Requisite seu <br/><span className="text-[#c17c0c]">Acesso Tático</span></h2>
            <p className="text-zinc-400 text-lg mb-8 leading-relaxed font-light">
              Devido à exclusividade territorial, cada solicitação passa por um filtro de inteligência antes da liberação das chaves de acesso.
            </p>
            
            <div className="space-y-6">
              <div className="flex gap-4 group">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-500 font-mono text-xs group-hover:border-[#c17c0c] transition-colors">01</div>
                <div>
                  <h4 className="font-bold uppercase text-xs tracking-widest text-zinc-200">Registro na Fila de Espera</h4>
                  <p className="text-sm text-zinc-500">Seus dados são sincronizados com nossa base de controle tático (Sheets).</p>
                </div>
              </div>
              <div className="flex gap-4 group">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-500 font-mono text-xs group-hover:border-[#c17c0c] transition-colors">02</div>
                <div>
                  <h4 className="font-bold uppercase text-xs tracking-widest text-zinc-200">Abertura de Canal Seguro</h4>
                  <p className="text-sm text-zinc-500">Conexão direta via WhatsApp para verificação de identidade parlamentar.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-zinc-950 border border-zinc-900 p-8 sm:p-12 rounded-3xl relative">
            <div className="absolute top-4 right-8 flex items-center space-x-1">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></div>
                <span className="text-[8px] font-mono text-red-500 uppercase tracking-tighter">Live Connection</span>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-2 ml-1">Identificação</label>
                  <input 
                    required
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    type="text" 
                    placeholder="Nome Completo" 
                    className="w-full bg-zinc-900/50 border border-zinc-800 rounded-xl px-4 py-3 text-white placeholder:text-zinc-700 focus:outline-none focus:border-[#c17c0c] transition-colors font-sans"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-2 ml-1">E-mail</label>
                  <input 
                    required
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    type="email" 
                    placeholder="E-mail profissional" 
                    className="w-full bg-zinc-900/50 border border-zinc-800 rounded-xl px-4 py-3 text-white placeholder:text-zinc-700 focus:outline-none focus:border-[#c17c0c] transition-colors font-sans"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-2 ml-1">Sua Atuação</label>
                  <select 
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="w-full bg-zinc-900/50 border border-zinc-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#c17c0c] transition-colors appearance-none font-sans"
                  >
                    <option value="">Selecione...</option>
                    <option value="Parlamentar em Mandato">Parlamentar em Mandato</option>
                    <option value="Candidato / Pretenso">Candidato / Pretenso</option>
                    <option value="Chefe de Gabinete">Chefe de Gabinete</option>
                    <option value="Assessor Estratégico">Assessor Estratégico</option>
                    <option value="Consultor / Marketing">Consultor / Marketing</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-2 ml-1">Território (UF/Cidade)</label>
                  <input 
                    required
                    name="region"
                    value={formData.region}
                    onChange={handleInputChange}
                    type="text" 
                    placeholder="Ex: MG / Belo Horizonte" 
                    className="w-full bg-zinc-900/50 border border-zinc-800 rounded-xl px-4 py-3 text-white placeholder:text-zinc-700 focus:outline-none focus:border-[#c17c0c] transition-colors font-sans"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-mono text-zinc-500 uppercase tracking-widest mb-2 ml-1">Mensagem Estratégica</label>
                <textarea 
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={3} 
                  placeholder="Descreva brevemente sua necessidade..." 
                  className="w-full bg-zinc-900/50 border border-zinc-800 rounded-xl px-4 py-3 text-white placeholder:text-zinc-700 focus:outline-none focus:border-[#c17c0c] transition-colors resize-none font-sans"
                ></textarea>
              </div>

              <button 
                type="submit"
                disabled={formState === 'loading'}
                className="w-full bg-gradient-to-r from-[#c17c0c] to-[#a66a0a] hover:from-[#e08e0e] hover:to-[#c17c0c] disabled:bg-zinc-800 disabled:text-zinc-500 disabled:cursor-not-allowed text-white px-8 py-5 rounded-xl text-lg font-bold uppercase tracking-[0.2em] transition-all shadow-[0_0_20px_rgba(193,124,12,0.3)] hover:shadow-[0_0_30px_rgba(193,124,12,0.5)] flex items-center justify-center space-x-3 group relative overflow-hidden focus:outline-none"
              >
                {formState === 'loading' ? (
                  <>
                    <Loader2 className="animate-spin" size={24} />
                    <span className="font-mono text-xs">Transmitindo para War Room...</span>
                  </>
                ) : (
                  <>
                    <span className="relative z-10">Enviar & Abrir WhatsApp</span>
                    <MessageSquare size={20} className="relative z-10 group-hover:scale-110 transition-transform" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WaitlistSection;
